import { FormSelectField, type FormSelectFieldProps } from '@mdvip/shared/components/shared/FormSelectField';
import { FormTextField, type FormTextFieldProps } from '@mdvip/shared/components/shared/FormTextField';
import { InfoDot } from '@mdvip/shared/components/shared/InfoDot';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@mdvip/shared/components/ui/radio-group';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PaymentAccountFormValues } from '@/lib/payments/paymentAccount.types';

type AccountScope = 'card' | 'bank';

// Validation messages are dynamic i18n keys (from the zod schema), so bypass the strict typed-t.
function useTranslateError() {
  const { t } = useTranslation('members');
  return t as unknown as (message: string) => string;
}

/**
 * Members-scoped `FormTextField`: binds the 'members' i18n namespace so validation messages
 * render translated, and fixes the form-values type. Call sites stay free of i18n wiring.
 */
export function TextField(props: Omit<FormTextFieldProps<PaymentAccountFormValues>, 'translateError'>) {
  const translateError = useTranslateError();
  return <FormTextField<PaymentAccountFormValues> {...props} translateError={translateError} />;
}

/** Members-scoped `FormSelectField` (mirror of {@link TextField}). */
export function SelectField(props: Omit<FormSelectFieldProps<PaymentAccountFormValues>, 'translateError'>) {
  const translateError = useTranslateError();
  return <FormSelectField<PaymentAccountFormValues> {...props} translateError={translateError} />;
}

/** HSA Yes/No radio bound to a boolean field. */
export function HsaRadioField({ scope }: { scope: AccountScope }) {
  const { control } = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');

  return (
    <Controller
      control={control}
      name={`${scope}.isHsa`}
      render={({ field }) => (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
            {t('payments.addAccount.hsa')}
            <InfoDot text={t('payments.addAccount.hsaInfo')} />
          </span>
          <RadioGroup
            className="flex w-auto flex-row gap-4"
            value={field.value ? 'yes' : 'no'}
            onValueChange={(value) => field.onChange(value === 'yes')}
          >
            <label htmlFor={`${scope}-hsa-yes`} className="flex items-center gap-2 text-sm">
              <RadioGroupItem id={`${scope}-hsa-yes`} value="yes" /> {t('payments.addAccount.yes')}
            </label>
            <label htmlFor={`${scope}-hsa-no`} className="flex items-center gap-2 text-sm">
              <RadioGroupItem id={`${scope}-hsa-no`} value="no" /> {t('payments.addAccount.no')}
            </label>
          </RadioGroup>
        </div>
      )}
    />
  );
}

/** Make-default + AutoPay checkboxes. AutoPay locks on when already enrolled; turning it on forces default. */
export function DefaultAndAutoPayFields({ scope, autoPayEnrolled }: { scope: AccountScope; autoPayEnrolled: boolean }) {
  const { control, setValue } = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');

  const defaultId = `${scope}-make-default`;
  const autoPayId = `${scope}-autopay`;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2.5">
        <Controller
          control={control}
          name={`${scope}.makeDefault`}
          render={({ field }) => (
            <Checkbox
              id={defaultId}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
          )}
        />
        <label htmlFor={defaultId} className="text-sm text-muted-foreground">
          {t('payments.addAccount.makeDefault')}
        </label>
      </div>
      <div className="flex items-center gap-2.5">
        <Controller
          control={control}
          name={`${scope}.autoPay`}
          render={({ field }) => (
            <Checkbox
              id={autoPayId}
              disabled={autoPayEnrolled}
              checked={autoPayEnrolled ? true : field.value}
              onCheckedChange={(checked) => {
                const on = checked === true;
                field.onChange(on);
                if (on) setValue(`${scope}.makeDefault`, true);
              }}
            />
          )}
        />
        <label htmlFor={autoPayId} className="text-sm text-muted-foreground">
          {t('payments.addAccount.autoPay')}
        </label>
        <InfoDot text={t('payments.addAccount.autoPayInfo')} />
      </div>
    </div>
  );
}
